import {
  Typography,
  styled,
  Box,
  ClickAwayListener,
  Fade,
  Popper,
  Icon,
  useTheme,
} from "@mui/material";
import { useState, useRef, useEffect, useContext } from "react";
import React from "react";
import { AuthContext } from "src/utils/AuthContext";
import { customEvent } from "src/utils/gtag";
import { useDimensions } from "src/utils/useDimensions.js";

const FeatureTitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  cursor: "help",
  "& .info-icon": {
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    flexShrink: 0,
  },
  "&:hover .info-icon": {
    opacity: 1,
  },
  [theme.breakpoints.down("md")]: {
    "& .info-icon": {
      opacity: 1, // Always visible on mobile
    },
  },
}));

const StyledTooltip = styled(Box)(({ theme, $isAbove, $showArrow }) => ({
  backgroundColor: theme.palette.darkBg,
  color: theme.palette.secondary.main,
  padding: theme.spacing(1.5, 2),
  borderRadius: "8px",
  maxWidth: "280px",
  fontSize: "0.875rem",
  lineHeight: 1.4,
  boxShadow: theme.shadows.tooltip(theme.palette),
  border: `1px solid ${theme.palette.overlay.white10}`,
  position: "relative",
  zIndex: 2000,
  [theme.breakpoints.down("sm")]: {
    maxWidth: "250px",
    fontSize: "0.8rem",
    padding: theme.spacing(1.5, 1.5),
  },
  ...($showArrow && {
    "&::before": {
      content: '""',
      position: "absolute",
      left: "20px",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      ...($isAbove
        ? {
            // Arrow pointing down when tooltip is above
            bottom: "-6px",
            borderTop: `6px solid ${theme.palette.darkBg}`,
          }
        : {
            // Arrow pointing up when tooltip is below
            top: "-6px",
            borderBottom: `6px solid ${theme.palette.darkBg}`,
          }),
    },
  }),
}));

export const ExplainerTooltip = ({ children, explainer, title, showArrow = true, forceOpen = false }) => {
  const [open, setOpen] = useState(forceOpen);
  const [anchorEl, setAnchorEl] = useState(null);
  const timerRef = useRef(null);
  const { user } = useContext(AuthContext);
  const { isMediumScreen } = useDimensions();
  const containerRef = useRef(null);

  useEffect(() => {
    if (forceOpen && containerRef.current) {
      setAnchorEl(containerRef.current);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (open) {
      customEvent({
        name: "user_open_plan_comparison_table_tooltip",
        category: "Comparison Table",
        label: "User open plan comparison table tooltip",
        data: {
          user_id: user?._id ?? "guest",
          user_email: user?.email ?? "-",
          user_handle: user?.handle ?? "no_handle",
        },
      });
    }
  }, [open]);

  const handleMouseEnter = (event) => {
    if (isMediumScreen) return;

    clearTimeout(timerRef.current);
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    if (isMediumScreen) return;

    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const handleClick = (event) => {
    if (!isMediumScreen) return;

    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClickAway = () => {
    if (isMediumScreen || forceOpen) {
      setOpen(false);
    }
  };

  if (!explainer) {
    return children;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <FeatureTitleContainer
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          {children}
        </FeatureTitleContainer>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
          style={{ zIndex: 2000 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, showArrow ? 8 : 4],
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 16,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top-start", "bottom-end", "top-end"],
              },
            },
          ]}
        >
          {({ TransitionProps, placement }) => (
            <Fade {...TransitionProps} timeout={150}>
              <StyledTooltip 
                $isAbove={placement && placement.includes("top")} 
                $showArrow={showArrow}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {explainer}
                </Typography>
              </StyledTooltip>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
